import React from "react";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { AnalyticsPeriod } from "../useAnalyticsPeriod";
import { PERIOD_PRESETS, PeriodPreset, addDays, colomboDay, longDate } from "../analyticsUtils";
import { SELECT } from "./ui";

interface Props {
  period: AnalyticsPeriod;
  /** Extra controls rendered beside the picker (e.g. a doctor filter). */
  children?: React.ReactNode;
}

const INPUT =
  "h-9 rounded-lg border border-slate-200 bg-white px-2.5 text-xs text-slate-700 outline-none transition-all focus:border-primary focus:ring-1 focus:ring-primary/20 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:[color-scheme:dark]";

/** Preset dropdown + custom range. Data ends yesterday, so neither input accepts today. */
export default function PeriodPicker({ period, children }: Props) {
  const yesterday = addDays(colomboDay(), -1);
  return (
    <div className="rounded-2xl border bg-white border-slate-100 shadow-sm dark:bg-slate-800 dark:border-slate-700 px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3">
      <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
        <CalendarMonthIcon sx={{ fontSize: 18 }} />
        <span className="text-xs font-medium">Period</span>
      </div>
      <select
        aria-label="Period preset"
        className={SELECT}
        value={period.preset}
        onChange={(e) => period.setPreset(e.target.value as PeriodPreset)}
      >
        {PERIOD_PRESETS.map((p) => (
          <option key={p.key} value={p.key}>
            {p.label}
          </option>
        ))}
      </select>
      {period.preset === "custom" && (
        <div className="flex items-center gap-2">
          <input
            type="date"
            aria-label="From"
            className={INPUT}
            value={period.from}
            max={period.to}
            onChange={(e) => period.setCustom({ from: e.target.value })}
          />
          <span className="text-xs text-slate-400">to</span>
          <input
            type="date"
            aria-label="To"
            className={INPUT}
            value={period.to}
            min={period.from}
            max={yesterday}
            onChange={(e) => period.setCustom({ to: e.target.value })}
          />
        </div>
      )}
      <span className="text-xs text-slate-400 dark:text-slate-500">
        {longDate(period.from)} – {longDate(period.to)} · compared with the period before
      </span>
      {children && <div className="sm:ml-auto flex items-center gap-2">{children}</div>}
    </div>
  );
}
